<app-report-shell
  [user]="user"
  [currentTime]="''"
  [sidebarCollapsed]="sidebarCollapsed"
  [mobileSidebarOpen]="mobileSidebarOpen"
  (menuToggle)="handleMenuToggle()"
  (closeMobileSidebar)="closeMobileSidebar()"
>
  <main class="daily-report-page log-sheet-page">
    <section class="log-sheet-page__panel app-card">
      <div class="log-sheet-page__panel-header">
        <div class="log-sheet-page__heading-copy">
          <h2 class="app-title-serif">Daily Report</h2>
        </div>
      </div>

      <div class="log-sheet-page__panel-body">
        <div class="log-sheet-page__filters-row">
          <form class="log-sheet-filters" [formGroup]="filtersForm">
            <label class="app-form-field log-sheet-filters__field" [attr.for]="isBsDateMode ? 'daily-report-bs-date-button' : 'daily-report-ad-date-input'">
              <span class="app-form-label">Select Date</span>
              <div class="log-sheet-filters__input-shell log-sheet-filters__input-shell--bs" #bsDatePickerShell>
                @if (isBsDateMode) {
                  <button
                    type="button"
                    id="daily-report-bs-date-button"
                    class="app-form-input log-sheet-filters__date-input log-sheet-filters__date-input--display"
                    aria-haspopup="dialog"
                    [attr.aria-expanded]="bsCalendarOpen"
                    (click)="openDatePicker()"
                  >
                    <span>{{ selectedDateDisplayLabel }}</span>
                    <span class="log-sheet-filters__date-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="5" width="18" height="16" rx="2"></rect>
                        <path d="M8 3v4"></path>
                        <path d="M16 3v4"></path>
                        <path d="M3 11h18"></path>
                      </svg>
                    </span>
                  </button>
                  @if (bsCalendarOpen) {
                    <dialog class="log-sheet-bs-calendar" open aria-label="Nepali calendar date picker">
                      <div class="log-sheet-bs-calendar__header">
                        <button type="button" class="log-sheet-bs-calendar__nav" aria-label="Previous month" (click)="shiftBsCalendarMonth(-1)">‹</button>
                        <div class="log-sheet-bs-calendar__title">
                          {{ bsCalendarMonth?.bs_month_name || 'Nepali Calendar' }} {{ bsCalendarMonth?.bs_year || '' }}
                        </div>
                        <button type="button" class="log-sheet-bs-calendar__nav" aria-label="Next month" (click)="shiftBsCalendarMonth(1)">›</button>
                      </div>
                      <div class="log-sheet-bs-calendar__weekdays">
                        @for (weekday of bsCalendarWeekdays; track weekday) {
                          <span>{{ weekday }}</span>
                        }
                      </div>
                      @if (bsCalendarLoading) {
                        <div class="log-sheet-bs-calendar__loading">Loading calendar...</div>
                      } @else {
                        <div class="log-sheet-bs-calendar__grid">
                          @for (cell of (bsCalendarMonth?.cells || []); track cell.bs_date) {
                            <button
                              type="button"
                              class="log-sheet-bs-calendar__day"
                              [class.log-sheet-bs-calendar__day--muted]="!cell.in_current_month"
                              [class.log-sheet-bs-calendar__day--selected]="cell.is_selected"
                              [class.log-sheet-bs-calendar__day--today]="cell.is_today"
                              (click)="selectBsCalendarCell(cell)"
                            >
                              {{ cell.bs_date_label }}
                            </button>
                          }
                        </div>
                      }
                    </dialog>
                  }
                  <input #hiddenDateInput id="daily-report-bs-date-input" class="log-sheet-filters__hidden-date-input" type="date" formControlName="date" />
                } @else {
                  <input #hiddenDateInput id="daily-report-ad-date-input" class="app-form-input log-sheet-filters__date-input" type="date" formControlName="date" />
                }
              </div>
            </label>

            <label class="app-form-field log-sheet-filters__field" for="daily-report-generator-autocomplete">
              <span class="app-form-label">Select Generator</span>
              <div class="log-sheet-filters__select-shell">
                <p-autocomplete
                  inputId="daily-report-generator-autocomplete"
                  [(ngModel)]="generatorSearchTerm"
                  [ngModelOptions]="{ standalone: true }"
                  [dropdown]="true"
                  size="compact"
                  [suggestions]="filteredGeneratorOptions"
                  placeholder="Select Generator"
                  (completeMethod)="onGeneratorSearchInput($event)"
                  (fieldBlur)="onGeneratorSearchBlur()"
                ></p-autocomplete>
              </div>
            </label>

            <div>
              <button
                type="button"
                class="app-button app-button--secondary equal app-filter-actions__button"
                [disabled]="isLoading"
                (click)="clearFilters()"
              >
                Clear
              </button>
            </div>
          </form>

          <div class="log-sheet-page__detail-row" aria-label="Daily report sections">
            <div class="log-sheet-page__detail-buttons" role="tablist">
              @for (tab of tabs; track tab.key) {
                <button
                  type="button"
                  class="log-sheet-page__detail-button"
                  [class.log-sheet-page__detail-button--active]="selectedTab === tab.key"
                  (click)="setTab(tab.key)"
                >
                  <span class="log-sheet-page__detail-button-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M6 3h9l3 3v15H6z"></path>
                      <path d="M9 12h6"></path>
                      <path d="M9 16h6"></path>
                    </svg>
                  </span>
                  <span>{{ tab.label }}</span>
                </button>
              }
            </div>
          </div>
        </div>

        <div class="daily-report-page__date-label">Daily Report as on {{ selectedDateDisplayLabel }}</div>

        @if (selectedTab === 'summary') {
          <div class="daily-report-summary-wrap app-table-scroll app-scrollbar-x">
            <table class="daily-report-summary-table">
              <thead>
                <tr>
                  <th scope="col" class="daily-report-summary-table__label-header"></th>
                  <th scope="col" class="daily-report-summary-table__group-header" colspan="2">Peak Details</th>
                  <th scope="col" class="daily-report-summary-table__group-header" colspan="2">Energy Details (MWh)</th>
                  <th scope="col" class="daily-report-summary-table__group-header" rowspan="2">Average (MW)</th>
                </tr>
                <tr>
                  <th scope="col" class="daily-report-summary-table__label-header"></th>
                  <th scope="col" class="daily-report-summary-table__metric-header">Date {{ getSummaryPeakTimeLabel(false) }}</th>
                  <th scope="col" class="daily-report-summary-table__metric-header">Last year {{ getSummaryPeakTimeLabel(true) }}</th>
                  <th scope="col" class="daily-report-summary-table__metric-header">Date</th>
                  <th scope="col" class="daily-report-summary-table__metric-header">Last year</th>
                </tr>
              </thead>
              <tbody>
                @if (isLoading) {
                  <tr>
                    <td colspan="6" class="log-sheet-page__empty-state">Loading daily report data...</td>
                  </tr>
                } @else {
                  @for (row of getSummaryTableRows(); track trackBySummaryTableRow($index, row)) {
                    <tr [class]="getSummaryRowClass(row)">
                      <td class="daily-report-summary-table__label-cell">{{ row.label }}</td>
                      <td class="daily-report-summary-table__metric-cell">{{ getSummaryCellText(row, 'peakDate') }}</td>
                      <td class="daily-report-summary-table__metric-cell">{{ getSummaryCellText(row, 'peakLastYear') }}</td>
                      <td class="daily-report-summary-table__metric-cell">{{ getSummaryCellText(row, 'energyDate') }}</td>
                      <td class="daily-report-summary-table__metric-cell">{{ getSummaryCellText(row, 'energyLastYear') }}</td>
                      <td class="daily-report-summary-table__metric-cell">{{ getSummaryCellText(row, 'average') }}</td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        } @else if (selectedTab === 'section-2') {
          <div class="daily-report-section-two-wrap app-table-scroll app-scrollbar-x">
            <table class="daily-report-section-two-table">
              <thead>
                <tr>
                  <th scope="col" class="daily-report-section-two-table__label-header"></th>
                  @for (column of sectionTwoColumns; track trackBySectionTwoColumn($index, column)) {
                    <th scope="col" class="daily-report-section-two-table__metric-header">{{ column }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of getSectionTwoRows(); track trackBySectionTwoRow($index, row)) {
                  <tr
                    [class.daily-report-section-two-table__row--highlighted]="row.highlighted"
                    [class.daily-report-section-two-table__row--child]="row.child"
                  >
                    <td class="daily-report-section-two-table__label-cell">
                      @if (hasSectionTwoChildren(row)) {
                        <button
                          type="button"
                          class="log-sheet-table__hierarchy-toggle"
                          [attr.aria-label]="(isSectionTwoRowExpanded(row) ? 'Collapse' : 'Expand') + ' ' + row.label"
                          (click)="toggleSectionTwoRow(row); $event.stopPropagation()"
                        >
                          <span aria-hidden="true">{{ isSectionTwoRowExpanded(row) ? '-' : '+' }}</span>
                        </button>
                      } @else if (row.child) {
                        <span class="log-sheet-table__hierarchy-toggle log-sheet-table__hierarchy-toggle--placeholder" aria-hidden="true"></span>
                      }
                      <span>{{ row.label }}</span>
                    </td>
                    @for (column of sectionTwoColumns; track trackBySectionTwoColumn($index, column)) {
                      <td class="daily-report-section-two-table__metric-cell">{{ getSectionTwoCellText(row, column) }}</td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="log-sheet-page__table-wrap app-table-scroll app-scrollbar-x">
            <table class="daily-report-table log-sheet-table">
              <thead>
                <tr>
                  <th scope="col" class="log-sheet-table__sticky-column">Particulars</th>
                  @for (column of columns; track trackByColumn($index, column)) {
                    <th scope="col" class="daily-report-table__metric-header">{{ column.label }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @if (isLoading) {
                  <tr>
                    <td [attr.colspan]="getEmptyColspan()" class="log-sheet-page__empty-state">Loading daily report data...</td>
                  </tr>
                } @else if (rows.length) {
                  @for (row of rows; track trackByRow($index, row)) {
                    <tr [class]="'log-sheet-table__hierarchy-row log-sheet-table__hierarchy-row--' + (row.row_kind || 'data')">
                      <td class="log-sheet-table__sticky-column">
                        <div [class]="'log-sheet-table__station log-sheet-table__station--level-' + (row.row_level ?? 0)">
                          @if (hasHierarchyChildren(row)) {
                            <button
                              type="button"
                              class="log-sheet-table__hierarchy-toggle"
                              [attr.aria-label]="(isHierarchyExpanded(row) ? 'Collapse' : 'Expand') + ' ' + row.station_name"
                              (click)="toggleHierarchyRow(row); $event.stopPropagation()"
                            >
                              <span aria-hidden="true">{{ isHierarchyExpanded(row) ? '-' : '+' }}</span>
                            </button>
                          } @else if (row.parent_row_key) {
                            <span class="log-sheet-table__hierarchy-toggle log-sheet-table__hierarchy-toggle--placeholder" aria-hidden="true"></span>
                          }
                          @if ((row.row_kind || 'data') === 'data' || row.parent_row_key) {
                            <span>{{ row.station_name }}</span>
                          } @else {
                            <strong>{{ row.station_name }}</strong>
                          }
                        </div>
                      </td>
                      @for (column of columns; track trackByColumn($index, column)) {
                        <td class="daily-report-table__metric-cell">{{ getCellText(row, column) }}</td>
                      }
                    </tr>
                  }
                } @else {
                  <tr>
                    <td [attr.colspan]="getEmptyColspan()" class="log-sheet-page__empty-state">No daily report data found</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </section>
  </main>
</app-report-shell>
